import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import {
  paginateTreatments,
  TreatmentPaginationPayload,
  deleteTreatment,
  getTreatmentResultOptions,
  TreatmentResultOptions,
} from "../../api/treatment";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";

const defaultPayload: TreatmentPaginationPayload = {
  curPage: 1,
  perPage: 10,
  sortBy: "created_on",
  direction: "desc",
  whereClause: [],
};

const TreatmentList: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [payload, setPayload] =
    useState<TreatmentPaginationPayload>(defaultPayload);
  const lastFetchKeyRef = useRef<string>("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Record<string, HTMLButtonElement>>({});
  const [showResultModal, setShowResultModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [treatmentId, setTreatmentId] = useState<string>("");
  const [existingImages, setExistingImages] = useState<TreatmentResultOptions | null>(null);
  const [loadingImages, setLoadingImages] = useState(false);
  const [selectedBeforeExisting, setSelectedBeforeExisting] = useState<string[]>([]);
  const [selectedAfterExisting, setSelectedAfterExisting] = useState<string[]>([]);
  const [previewBeforeImages, setPreviewBeforeImages] = useState<Array<{ id: string; url: string }>>([]);
  const [previewAfterImages, setPreviewAfterImages] = useState<Array<{ id: string; url: string }>>([]);
  const totalPages = Math.max(1, Math.ceil(total / payload.perPage));

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await paginateTreatments(payload);
      const data = res?.data?.data || res?.data || {};
      setItems(
        Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : []
      );
      setTotal(data.total || res.data.total || 0);
    } catch (err) {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const key = `${payload.curPage}|${payload.perPage}|${payload.sortBy}|${payload.direction}|${JSON.stringify(
      payload.whereClause || []
    )}`;
    if (lastFetchKeyRef.current === key) return;
    lastFetchKeyRef.current = key;
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payload.curPage,
    payload.perPage,
    payload.sortBy,
    payload.direction,
    payload.whereClause,
  ]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Check if click is outside the open menu and button
      const isClickOnButton = Object.values(buttonRefs.current).some(
        (btn) => btn && btn.contains(target)
      );
      const isClickOnMenu = target.closest('.dropdown-menu-container');

      if (!isClickOnButton && !isClickOnMenu && openMenuId) {
        setOpenMenuId(null);
        setDropdownPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuId]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPayload((prev) => ({
      ...prev,
      curPage: 1,
      whereClause: value
        ? [
          {
            key: "patientName",
            value,
            operator: "=",
          },
        ]
        : [],
    }));
  };

  const goToPage = (p: number) => {
    setPayload((prev) => ({
      ...prev,
      curPage: Math.min(Math.max(1, p), totalPages),
    }));
  };

  const changePerPage = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10) || 10;
    setPayload((prev) => ({ ...prev, perPage: value, curPage: 1 }));
  };

  const handleConfirm = async (id: string) => {
    try {
      setLoading(true);
      await deleteTreatment(id);
      setShowConfirm(false);
      setShowSuccess(true);
      fetchItems();
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to delete treatment.");
    } finally {
      setLoading(false);
    }
  };


  const handleViewResult = async (treatment: any) => {
    const docTreatmentId = treatment.id || treatment._id || "";
    if (!docTreatmentId) {
      toast.error("Treatment ID not found");
      return;
    }

    setTreatmentId(docTreatmentId);
    setSelectedBeforeExisting([]);
    setSelectedAfterExisting([]);
    setShowResultModal(true);
    setLoadingImages(true);

    try {
      const result = await getTreatmentResultOptions(docTreatmentId);
      setExistingImages(result);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to load existing images");
      setExistingImages(null);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleCreateTreatmentRecord = (treatment: any) => {
    const treatmentId = treatment.id || treatment._id || "";
    const patientId = treatment.patientId || treatment.patient?.id || "";
    const clinicId = treatment.clinicId || treatment.clinic?.id || treatment.patient?.clinic?.id || "";

    if (!treatmentId) {
      toast.error("Treatment ID not found");
      return;
    }

    if (!patientId) {
      toast.error("Patient ID not found");
      return;
    }

    if (!clinicId) {
      toast.error("Clinic ID not found");
      return;
    }

    // Navigate to treatment record form with query parameters to pre-fill the form
    const queryParams = new URLSearchParams({
      treatmentId,
      patientId,
      clinicId
    });

    navigate(`/treatment-record/create?${queryParams.toString()}`);
  };

  const handleCreateTreatmentPlan = (treatment: any) => {
    const patientId = treatment.patientId || treatment.patient?.id || "";

    if (!patientId) {
      toast.error("Patient ID not found");
      return;
    }

    // Navigate to treatment plan form with query parameters to pre-fill the patient
    const queryParams = new URLSearchParams({
      patientId
    });

    navigate(`/treatment-plan/create?${queryParams.toString()}`);
  };

  const handleUploadAndPreview = () => {
    // Get selected images for preview
    const beforeImages: Array<{ id: string; url: string }> = [];
    const afterImages: Array<{ id: string; url: string }> = [];

    if (selectedBeforeExisting.length > 0 && existingImages?.beforeOptions) {
      selectedBeforeExisting.forEach((imgId) => {
        const selectedImg = existingImages.beforeOptions.find(img => img.id === imgId);
        if (selectedImg) {
          beforeImages.push(selectedImg);
        }
      });
    }

    if (selectedAfterExisting.length > 0 && existingImages?.afterOptions) {
      selectedAfterExisting.forEach((imgId) => {
        const selectedImg = existingImages.afterOptions.find(img => img.id === imgId);
        if (selectedImg) {
          afterImages.push(selectedImg);
        }
      });
    }

    if (beforeImages.length === 0 && afterImages.length === 0) {
      toast.error("Please select at least one image to preview");
      return;
    }

    // Set preview images and open preview modal
    setPreviewBeforeImages(beforeImages);
    setPreviewAfterImages(afterImages);
    setShowPreviewModal(true);
  };

  const handleDownloadImage = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteAndClose = () => {
    setShowPreviewModal(false);
    setPreviewBeforeImages([]);
    setPreviewAfterImages([]);
    // Also clear selections in the main modal
    setSelectedBeforeExisting([]);
    setSelectedAfterExisting([]);
  };


  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}

      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8"
            >
              {/* Header Row with Search and New Button (similar to Treatment Plans) */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  Treatments
                </h2>
                <div className="flex flex-col md:flex-row md:items-center md:gap-4">

                  <button
                    className="mt-3 md:mt-0 px-4 py-2 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition"
                    onClick={() => navigate("/treatment")}
                  >
                    + New Treatment
                  </button>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-gray-600">
                  Total: <span className="font-medium">{total}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-full md:w-64">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      placeholder="Search by patient name..."
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <span className="text-sm text-gray-600">Rows:</span>
                  <select
                    className="border rounded-lg p-1 select-plain"
                    value={payload.perPage}
                    onChange={changePerPage}
                  >
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700  ">
                        Patient Name
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700  ">
                        Clinic Name
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700  ">
                        Treatment Type
                      </th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr>
                        <td
                          className="px-4 py-6 text-center text-red-500"
                          colSpan={4}
                        >
                          No records found
                        </td>
                      </tr>
                    ) : (
                      items.map((t: any) => (
                        <tr key={t.id || t._id} className="border-t">
                          <td className="px-4 py-3 text-sm text-gray-800">
                            {t.patient?.name ||
                              t.patientName ||
                              t.patientId ||
                              "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {t.patient?.clinic?.name ||
                              t.clinic?.name ||
                              t.clinicName ||
                              t.clinicId ||
                              "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {t.treatmentType === "EXTRACTION"
                              ? "Extraction"
                              : t.treatmentType === "NON_EXTRACTION"
                                ? "No Extraction"
                                : t.treatmentType || "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div
                              className="relative"
                              ref={(el) => {
                                const itemId = t.id || t._id || "";
                                if (openMenuId === itemId) {
                                  (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                }
                              }}
                            >
                              <button
                                ref={(el) => {
                                  if (el) {
                                    const itemId = t.id || t._id || "";
                                    buttonRefs.current[itemId] = el;
                                  }
                                }}
                                className="text-gray-600 hover:text-gray-900 focus:outline-none p-1 rounded-full hover:bg-gray-100"
                                onClick={(e) => {
                                  const itemId = t.id || t._id || "";
                                  if (openMenuId === itemId) {
                                    setOpenMenuId(null);
                                    setDropdownPosition(null);
                                  } else {
                                    const button = e.currentTarget;
                                    const rect = button.getBoundingClientRect();
                                    setDropdownPosition({
                                      top: rect.bottom + window.scrollY + 4,
                                      left: rect.right + 4,
                                    });
                                    setOpenMenuId(itemId);
                                  }
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5"
                                  fill="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <circle cx="12" cy="5" r="2" />
                                  <circle cx="12" cy="12" r="2" />
                                  <circle cx="12" cy="19" r="2" />
                                </svg>
                              </button>
                              {openMenuId === (t.id || t._id) && dropdownPosition && createPortal(
                                <div
                                  className="dropdown-menu-container fixed w-32 bg-white rounded-lg shadow-lg border border-gray-200 z-[9999] overflow-hidden"
                                  style={{
                                    top: `${dropdownPosition.top}px`,
                                    left: `${dropdownPosition.left}px`,
                                  }}
                                >
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      navigate(`/treatment/${t.id || t._id || ""}`);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Edit Treatment
                                  </button>
                                  <div className="border-t border-gray-200"></div>

                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      handleCreateTreatmentPlan(t);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Treatment Plan
                                  </button>
                                  <div className="border-t border-gray-200"></div>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      handleCreateTreatmentRecord(t);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Treatment Record
                                  </button>

                                  <div className="border-t border-gray-200"></div>
                                  <button
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      handleViewResult(t);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    View Result
                                  </button>
                                  <div className="border-t border-gray-200"></div>
                                  {/* <button
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
                                    onClick={() => {
                                      setSelectedId(t.id || t._id || "");
                                      setShowConfirm(true);
                                      setOpenMenuId(null);
                                      setDropdownPosition(null);
                                    }}
                                  >
                                    Delete
                                  </button> */}
                                </div>,
                                document.body
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <hr />
              {/* Pagination */}
              <div className="flex items-center justify-between mt-6">
                <div className="text-sm text-gray-600">
                  Page {payload.curPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(1)}
                    disabled={payload.curPage === 1}
                  >
                    « First
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage - 1)}
                    disabled={payload.curPage === 1}
                  >
                    ‹ Prev
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(payload.curPage + 1)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Next ›
                  </button>
                  <button
                    className="px-3 py-1.5 border rounded-lg text-sm hover:bg-gray-50"
                    onClick={() => goToPage(totalPages)}
                    disabled={payload.curPage >= totalPages}
                  >
                    Last »
                  </button>
                </div>
              </div>

              <ConfirmModal
                isOpen={showConfirm}
                title="Delete Treatment"
                message="Are you sure you want to delete this treatment?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                onConfirm={() => handleConfirm(selectedId)}
                onCancel={() => setShowConfirm(false)}
                loading={loading}
              />
              <SuccessModal
                isOpen={showSuccess}
                title="Treatment Deleted"
                message="The treatment has been deleted successfully."
                onClose={() => setShowSuccess(false)}
              />

              {/* Treatment Result Modal */}
              {showResultModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-800">
                        View Treatment Result
                      </h3>
                      <button
                        type="button"
                        onClick={() => {
                          setShowResultModal(false);
                          setExistingImages(null);
                          setSelectedBeforeExisting([]);
                          setSelectedAfterExisting([]);
                        }}
                        className="text-gray-500 hover:text-gray-700"
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>

                    {loadingImages ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-gray-500">Loading images...</div>
                      </div>
                    ) : (
                      <>
                        {/* Before Images Section */}
                        <div className="mb-8">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Attach before treatment images
                          </label>

                          {/* Box showing selected existing images */}
                          <div className="mb-4 border-2 border-red-500 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-600">
                                Selected Images ({selectedBeforeExisting.length})
                              </span>
                              {selectedBeforeExisting.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedBeforeExisting([])}
                                  className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition"
                                  title="Clear all selections"
                                >
                                  Clear All
                                </button>
                              )}
                            </div>
                            {selectedBeforeExisting.length > 0 && existingImages?.beforeOptions ? (
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {selectedBeforeExisting.map((imgId) => {
                                  const selectedImg = existingImages.beforeOptions.find(img => img.id === imgId);
                                  return selectedImg ? (
                                    <div key={imgId} className="relative border border-gray-200 rounded overflow-hidden bg-white">
                                      <img
                                        src={selectedImg.url}
                                        alt="Selected before treatment"
                                        className="w-full h-24 object-cover"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => setSelectedBeforeExisting(prev => prev.filter(id => id !== imgId))}
                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 transition"
                                        title="Remove this image"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            ) : (
                              <div className="w-full h-24 bg-white rounded border border-gray-200 flex items-center justify-center">
                                <p className="text-sm text-gray-400">No images selected</p>
                              </div>
                            )}
                          </div>

                          {/* Select from existing images */}
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Select from existing images
                            </label>
                            {existingImages?.beforeOptions && existingImages.beforeOptions.length > 0 ? (
                              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                                {existingImages.beforeOptions.map((img) => (
                                  <div
                                    key={img.id}
                                    className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition ${selectedBeforeExisting.includes(img.id)
                                      ? "border-blue-500 ring-2 ring-blue-200"
                                      : "border-gray-200 hover:border-gray-300"
                                      }`}
                                    onClick={() => {
                                      setSelectedBeforeExisting(prev =>
                                        prev.includes(img.id)
                                          ? prev.filter(id => id !== img.id)
                                          : [...prev, img.id]
                                      );
                                    }}
                                  >
                                    <img
                                      src={img.url}
                                      alt="Before treatment"
                                      className="w-full h-24 object-cover"
                                    />
                                    {selectedBeforeExisting.includes(img.id) && (
                                      <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                                        ✓
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 py-4 text-center border border-gray-200 rounded-lg bg-gray-50">
                                No existing before images available
                              </p>
                            )}
                          </div>
                        </div>

                        {/* After Images Section */}
                        <div className="mb-8">
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Attach after treatment images
                          </label>

                          {/* Box showing selected existing images */}
                          <div className="mb-4 border-2 border-red-500 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-gray-600">
                                Selected Images ({selectedAfterExisting.length})
                              </span>
                              {selectedAfterExisting.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedAfterExisting([])}
                                  className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition"
                                  title="Clear all selections"
                                >
                                  Clear All
                                </button>
                              )}
                            </div>
                            {selectedAfterExisting.length > 0 && existingImages?.afterOptions ? (
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {selectedAfterExisting.map((imgId) => {
                                  const selectedImg = existingImages.afterOptions.find(img => img.id === imgId);
                                  return selectedImg ? (
                                    <div key={imgId} className="relative border border-gray-200 rounded overflow-hidden bg-white">
                                      <img
                                        src={selectedImg.url}
                                        alt="Selected after treatment"
                                        className="w-full h-24 object-cover"
                                      />
                                      <button
                                        type="button"
                                        onClick={() => setSelectedAfterExisting(prev => prev.filter(id => id !== imgId))}
                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 transition"
                                        title="Remove this image"
                                      >
                                        ×
                                      </button>
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            ) : (
                              <div className="w-full h-24 bg-white rounded border border-gray-200 flex items-center justify-center">
                                <p className="text-sm text-gray-400">No images selected</p>
                              </div>
                            )}
                          </div>

                          {/* Select from existing images */}
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Select from existing images
                            </label>
                            {existingImages?.afterOptions && existingImages.afterOptions.length > 0 ? (
                              <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                                {existingImages.afterOptions.map((img) => (
                                  <div
                                    key={img.id}
                                    className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition ${selectedAfterExisting.includes(img.id)
                                      ? "border-blue-500 ring-2 ring-blue-200"
                                      : "border-gray-200 hover:border-gray-300"
                                      }`}
                                    onClick={() => {
                                      setSelectedAfterExisting(prev =>
                                        prev.includes(img.id)
                                          ? prev.filter(id => id !== img.id)
                                          : [...prev, img.id]
                                      );
                                    }}
                                  >
                                    <img
                                      src={img.url}
                                      alt="After treatment"
                                      className="w-full h-24 object-cover"
                                    />
                                    {selectedAfterExisting.includes(img.id) && (
                                      <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">
                                        ✓
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-sm text-gray-500 py-4 text-center border border-gray-200 rounded-lg bg-gray-50">
                                No existing after images available
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                          <button
                            type="button"
                            onClick={() => {
                              setShowResultModal(false);
                              setExistingImages(null);
                              setSelectedBeforeExisting([]);
                              setSelectedAfterExisting([]);
                            }}
                            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                          >
                            Close
                          </button>
                          <button
                            type="button"
                            onClick={handleUploadAndPreview}
                            disabled={
                              selectedBeforeExisting.length === 0 ||
                              selectedAfterExisting.length === 0
                            }
                            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Upload & Preview
                          </button>
                        </div>
                      </>
                    )}
                  </motion.div>
                </div>
              )}

              {/* Preview Results Modal */}
              {showPreviewModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto"
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-gray-800">
                        Preview Results
                      </h3>
                      <button
                        type="button"
                        onClick={handleDeleteAndClose}
                        className="text-gray-500 hover:text-gray-700 text-2xl"
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>

                    {/* Before Images Section */}
                    {previewBeforeImages.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-md font-medium text-gray-700 mb-3">
                          Before Images
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {previewBeforeImages.map((img) => {
                            const filename = img.url.split('/').pop() || 'image.jpg';
                            return (
                              <div key={img.id} className="relative border border-gray-200 rounded-lg overflow-hidden group">
                                <img
                                  src={img.url}
                                  alt="Before treatment"
                                  className="w-full h-48 object-cover"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadImage(img.url, filename)}
                                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition"
                                    title="Download"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleDeleteAndClose}
                                    className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition"
                                    title="Delete"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* After Images Section */}
                    {previewAfterImages.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-md font-medium text-gray-700 mb-3">
                          After Images
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {previewAfterImages.map((img) => {
                            const filename = img.url.split('/').pop() || 'image.jpg';
                            return (
                              <div key={img.id} className="relative border border-gray-200 rounded-lg overflow-hidden group">
                                <img
                                  src={img.url}
                                  alt="After treatment"
                                  className="w-full h-48 object-cover"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadImage(img.url, filename)}
                                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition"
                                    title="Download"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={handleDeleteAndClose}
                                    className="p-2 bg-red-500 rounded-full hover:bg-red-600 transition"
                                    title="Delete"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </div>
              )}
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default TreatmentList;
